import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { useResidentEnvironments } from '~/hooks/environment/useResidentEnvironments';
import { AnnotationRecord } from '~/domain/annotation/annotation';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { AnnotationListState } from '~/state/atoms/annotationListAtoms';
import { resolveSeriesLabel, SeriesLabel } from '~/utils/seriesLabel';

/** The annotations of one list placed on one image series, and its label. */
interface AnnotationSeriesGroup extends SeriesLabel {
    readonly seriesInstanceUid: string;
    /** Ids of the annotations on this series, oldest first. */
    readonly ids: readonly string[];
}

export interface AnnotationSeriesGroups {
    /** One group per series, in the order each series first appears. */
    readonly groups: readonly AnnotationSeriesGroup[];
    /**
     * Whether a list names the series of each group: true unless one
     * dataset is resident and every annotation is on it, where the image
     * is the only one there is.
     */
    readonly showSeriesLabels: boolean;
}

/**
 * The annotations of `list` grouped by the image series they were placed
 * on, each group labelled from the patient's images, so a list can tell
 * apart figures read from different images. A series that no tile shows is
 * still named; a series that is not among the patient's images gets the
 * generic label and a UID fragment.
 */
export function useAnnotationSeriesGroups<T extends AnnotationRecord>(
    list: AnnotationListState<T>
): AnnotationSeriesGroups {
    const items = useRecoilValue(list.items);
    const residents = useResidentEnvironments();
    const patient = useSelectedPatient();
    const intl = useIntl();

    const groups = useMemo<AnnotationSeriesGroup[]>(() => {
        const fallbackLabel = intl.formatMessage({ id: 'unknown.series' });
        const images = patient?.images ?? [];
        const idsBySeries = new Map<string, string[]>();
        for (const item of items) {
            const uid = item.plane.seriesInstanceUid;
            const ids = idsBySeries.get(uid);
            if (ids) {
                ids.push(item.id);
            } else {
                idsBySeries.set(uid, [item.id]);
            }
        }
        return Array.from(idsBySeries, ([seriesInstanceUid, ids]) => ({
            seriesInstanceUid,
            ids,
            ...resolveSeriesLabel(seriesInstanceUid, images, fallbackLabel),
        }));
    }, [items, patient, intl]);

    // With several datasets resident, or an annotation of a dataset no tile
    // shows, each group names its dataset.
    const shownSeriesUid =
        residents.length === 1
            ? residents[0].image?.parent.seriesUid
            : undefined;
    const showSeriesLabels = groups.some(
        (group) => group.seriesInstanceUid !== shownSeriesUid
    );

    return { groups, showSeriesLabels };
}
